<template>
  <div
    class="pb-wrapper"
    :class="rootStore.rootClasses"
  >
    <Transition>
      <div
        v-if="showFullLoader"
        class="pb-full-loader full-loader"
      >
        <Asset src="Images/Logos/SVG/Nicolaudie-profile-builder.svg" />
        <Spinner />
      </div>
    </Transition>
    <template v-if="translationsLoaded">
      <BaseBanner
        v-if="updateAvailable"
        level="info"
        class="txt-center"
      >
        {{ translate('pb2.mainView.updateAvailableBannerMessage') }}
        <BaseButton @click="update">
          {{ translate('pb2.mainView.updateAvailableBannerAction') }}
        </BaseButton>
      </BaseBanner>
      <BaseBanner
        v-if="releaseNotesState"
        level="success"
        removable
        @close="closeReleaseNotes"
      >
        Profile builder was updated !
        <BaseButton
          class="actionMessage"
          @click="showReleaseNotesDialog"
        >
          View release notes
        </BaseButton>
      </BaseBanner>
      <BaseBanner
        v-if="canRead && !canWrite && !profile.isCertificated && readonlyBannerState"
        removable
        level="warning"
        @close="hideReadonlyBanner"
      >
        {{ translate('pb2.mainView.readonlyProfileBannerMessage') }}
        <BaseButton
          :to="{
            name: routeNames.Properties,
            query: { edit: 'permissions', requestWrite: true },
          }"
        >
          {{ translate('pb2.mainView.readonlyProfileBannerAction') }}
        </BaseButton>
      </BaseBanner>
      <BaseBanner
        v-if="removed"
        level="warning"
      >
        {{ translate('pb2.mainView.profileInTrashBannerMessage') }}
        <BaseButton @click="profileStore.restore()">
          {{ translate('pb2.mainView.profileInTrashBannerAction') }}
        </BaseButton>
      </BaseBanner>
      <template v-if="canRead && profile.isCertificated">
        <BaseBanner v-if="canWrite" level="error">
          <strong>Warning !</strong> This is an official profile
        </BaseBanner>
        <BaseBanner v-else level="warning">
          {{ translate('pb2.mainView.officialProfileBannerMessage') }}
          <BaseButton @click="profileStore.duplicate()">
            {{ translate('pb2.mainView.officialProfileBannerAction') }}
          </BaseButton>
        </BaseBanner>
      </template>
      <UiBanners
        :banners="visibleBanners"
        teleport="#pb-dialogs"
        @close="closeBanner"
      />
    </template>

    <RouterView :name="routeViews.Header" />
    <div class="pb-main">
      <ErrorView
        v-if="rootStore.errorPage"
        v-bind="rootStore.errorPage || {}"
        :show-header="!translationsLoaded"
      />
      <RouterView
        v-else-if="loaded"
        :name="routeViews.Main"
      />
    </div>
    <Alerts
      position="alerter right bottom"
      group="main"
    />
    <EnvWatermark />
    <ReleaseNotesDialog
      v-if="!showFullLoader"
      v-bind="releaseNotesControl"
    />
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  mergeProps,
} from 'vue'

import Alerts from '@nicolaudie-group/ng-ui-kit/components/Alerts/Alerts.vue'
import Spinner from '@nicolaudie-group/ng-ui-kit/components/Spinner/Spinner.vue'
import Asset from '@nicolaudie-group/ng-ui-kit/components/Asset/Asset.vue'
import BaseBanner from '@nicolaudie-group/ng-ui-kit/components/BaseBanner/BaseBanner.vue'
import BaseButton from '@nicolaudie-group/ng-ui-kit/components/BaseButton/BaseButton.vue'
import UiBanners from '@nicolaudie-group/ng-ui-kit/components/UiBanners/UiBanners.vue'

import useToggle from '@nicolaudie-group/ng-ui-kit/composables/useToggle'
import useAlerter from '@nicolaudie-group/ng-ui-kit/composables/useAlerter'
import useVersionnedBanners from '@nicolaudie-group/ng-ui-kit/composables/useVersionnedBanners'

import setTranslate from '@nicolaudie-group/ng-ui-kit/helpers/useTranslate'
import dev from '@nicolaudie-group/ng-ui-kit/helpers/dev'
import { warn } from '@nicolaudie-group/ng-ui-kit/helpers/prod'
import isIn from '@nicolaudie-group/ng-ui-kit/helpers/isIn'
import { alertsToBanners } from '@nicolaudie-group/ng-ui-kit/helpers/alertToBanner'
import useDelay from '@nicolaudie-group/ng-ui-kit/composables/useDelay'
import useHotkeys, { KEYS } from '@nicolaudie-group/ng-ui-kit/composables/useHotkeys'

import PbError from '@/classes/PbError.class'

import { SDKTypes, v2 } from '@/helpers/sdk'
import translateHelper from '@/helpers/translate'

import routeViews from '@/router/views'
import routeNames from '@/router/names'

import useRootStore from '@/stores/root.store'
import useRouterStore from '@/stores/router.store'
import useProfileStore from '@/stores/profile.store'

import useServiceWorker from '@/composables/useServiceWorker'
import useTranslate from '@/composables/useTranslate'
import { useProfile } from '@/composables/useProfile'
import useLogin from '@/composables/useLogin'

import ErrorView from '@/views/ErrorView.vue'

import EnvWatermark from '@nicolaudie-group/ng-ui-kit/components/Watermark/EnvWatermark.vue'
import usePbTitle from '@/composables/usePbTitle'
import ReleaseNotesDialog from '@/components/ReleaseNotesDialog/ReleaseNotesDialog.vue'
import useReleaseNotes from '@/composables/useReleaseNotes'
import useAction from '@/composables/useAction'

const { delay, reset } = useDelay()

const rootStore = useRootStore()
const routerStore = useRouterStore()
const profileStore = useProfileStore()

// Give the translate method to the UI kit
setTranslate(translateHelper)
const { translationsLoaded, translate } = useTranslate()

// Update the page title when the route changes
usePbTitle()

const showFullLoader = ref(true)
const loaded = computed(() => routerStore.route.meta.loaded && routerStore.route.meta.loaded())
const failed = computed(() => routerStore.route.meta.failed && routerStore.route.meta.failed())

watch(loaded, () => {
  reset()
  if (loaded.value) {
    delay(() => {
      showFullLoader.value = false
    }, 500)
  } else {
    showFullLoader.value = true
  }
}, { immediate: true })

const login = useLogin()

const { error } = useAlerter()

const lastError = computed(() => rootStore.error)

watch(failed, () => {
  if (failed.value) {
    showFullLoader.value = false
    if (!rootStore.errorPage) {
      rootStore.addError(new PbError({
        title: 'Failed to load',
        message: 'Profile Builder could not load properly',
        page: true,
      }))
    }
  } else {
    rootStore.clearErrorPages()
  }
}, { immediate: true })

watch(lastError, (errorData) => {
  if (errorData) {
    const {
      title, message, page, status,
    } = errorData
    if (!page) {
      error({
        title,
        text: message,
      })
    }

    if (status === SDKTypes.HttpStatusCode.UNAUTHORIZED) {
      login()
    }
  }
})

const { updateAvailable, update } = useServiceWorker()

const {
  closeBanner: closeReleaseNotes,
  openDialog: showReleaseNotesDialog,
  bannerState: releaseNotesState,
  dialogControl: releaseNotesControl,
} = useReleaseNotes()

const profile = useProfile()

const canRead = computed(() => !!profile.value)
const canWrite = computed(() => canRead.value && profile.value.userCanEdit)

const removed = computed(() => profile.value && profile.value.isRemove)

const { off: hideReadonlyBanner, state: readonlyBannerState } = useToggle(true)

// Add a shortcut to toggle dev logs & variables exposition bypass
const { active: bPressed } = useHotkeys(KEYS.B, {
  shift: false,
  alt: false,
  meta: false,
  ctrl: false,
  exact: true,
  disableInInput: true,
  disableInTextarea: true,
  disableInButton: true,
})

const { action: loadAlerts, data: banners } = useAction(async () => {
  const result = await v2.core.getInfos()
  return alertsToBanners(result?.data?.alerts || [])
}, { shallow: true })

watch(loaded, (isLoaded) => {
  /*
  Load alerts only after required resources for the current
  route have been loaded
  */
  if (isLoaded) {
    loadAlerts()
  }
}, { immediate: true })

const {
  visibleBanners,
  closeBanner,
} = useVersionnedBanners(banners)

watch(bPressed, (value) => {
  if (!isIn.prod && value) {
    dev.configure({ bypass: !dev.bypass })
    warn(`Bypass ${dev.bypass ? 'enabled' : 'disabled'}`)
  }
})

watch(() => rootStore.rootClasses, (newRootClasses) => {
  const dialogsProps = mergeProps({ class: newRootClasses })
  document.getElementById('pb-dialogs').classList = dialogsProps.class
}, { immediate: true, deep: true })
</script>

<style lang="scss" src="./App.scss" />
